import React, { useEffect, useState } from 'react';
import { isPathBlockedForRole } from '../config/roleAccess';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { recordPageView } from '../services/deviceUsage';
import { canUseCrm, isCrmBlocked } from '../config/crm';
import { crmIdentity } from './CrmGate';
import { isKelly } from './KellyOnly';
import { refreshSellerTaskDates } from '../services/transactionTasks';
import ViewAsToggle from './ViewAsToggle';
import { useYoutubeStudioAccess } from '../config/youtubeStudio';
import { useInboxUnread } from '../pages/crm/InboxPage';

// Same side menu as the dashboard, so people can always get back home from any page.
// On phones the side menu becomes a top bar with a menu button.
const ITEMS = [
  { label: 'Agent Dashboard', path: '/dashboard' },
  { label: 'Tasks', path: '/tasks' },
  { label: 'Inbox', path: '/inbox', crm: true, inbox: true },
  {
    label: 'Leads',
    path: '/leads',
    crm: true,
    children: [
      { label: 'Contacts', path: '/leads/contacts' },
      { label: 'Lists', path: '/leads/lists' },
      { label: 'Email & Text Log', path: '/leads/messages-log' },
      { label: 'Campaigns', path: '/leads/campaigns' },
      { label: 'Email Builder', path: '/leads/email-builder' },
      { label: 'Power Dialer', path: '/leads/dialer' },
    ],
  },
  { label: 'Pipeline', path: '/pipeline', crm: true },
  { label: 'Transactions', path: '/transactions' },
  { label: 'Training', path: '/training' },
  {
    label: 'Marketing',
    path: '/marketing',
    children: [
      { label: 'Marketing Center', path: '/marketing' },
      { label: 'YouTube Studio', path: '/marketing/youtube-studio', youtubeStudio: true },
    ],
  },
  { label: 'Tools', path: '/tools' },
  { label: 'Open Houses', path: '/open-houses' },
  { label: 'Calendar', path: '/calendar' },
  { label: 'Performance', path: '/performance' },
  { label: 'Profile', path: '/profile' },
  { label: 'Website', path: '/website', comingSoon: true, kellyOnly: true },
  { label: 'Feedback', path: '/feedback' },
];

const sideNav = {
  width: '220px',
  flexShrink: 0,
  backgroundColor: '#1a1a1a',
  borderRight: '2px solid #D4AF37',
  padding: '24px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  position: 'sticky',
  top: 0,
  alignSelf: 'flex-start',
  height: '100vh',
  overflowY: 'auto',
  boxSizing: 'border-box',
};
const topBar = {
  position: 'sticky',
  top: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 16px',
  paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))',
  backgroundColor: '#1a1a1a',
  borderBottom: '2px solid #D4AF37',
  boxSizing: 'border-box',
};
const menuToggle = {
  minWidth: 44,
  minHeight: 44,
  padding: '0 12px',
  backgroundColor: 'transparent',
  color: '#D4AF37',
  border: '1px solid #D4AF37',
  borderRadius: '6px',
  fontSize: '18px',
  cursor: 'pointer',
};
const drawer = {
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  width: 'min(300px, 85vw)',
  zIndex: 10001,
  backgroundColor: '#1a1a1a',
  padding: '16px',
  paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
  paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  overflowY: 'auto',
  boxSizing: 'border-box',
};
const label = { fontSize: '12px', color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' };
const button = {
  width: '100%',
  minHeight: 44,
  textAlign: 'left',
  padding: '12px 14px',
  backgroundColor: 'transparent',
  color: '#FFFFFF',
  border: '1px solid #333333',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600',
  fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  boxSizing: 'border-box',
};
const NAV_CSS = `
  .app-nav-btn:not(.app-nav-active) { border: 1px solid #333333 !important; box-shadow: none !important; }
  .app-nav-btn:not(.app-nav-active):hover, .app-nav-btn:not(.app-nav-active):focus, .app-nav-btn:not(.app-nav-active):focus-visible, .app-nav-btn:not(.app-nav-active):active { border: 1px solid #333333 !important; box-shadow: none !important; outline: none !important; }
  .app-nav-btn.app-nav-active:focus-visible { outline: 2px solid #D4AF37; outline-offset: 2px; }
`;
// Label + badge stay on one line: the label truncates with an ellipsis, the badge never wraps or shrinks.
const rowLine = { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, whiteSpace: 'nowrap' };
const labelTrunc = { flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const soonBadge = { flex: '0 0 auto', whiteSpace: 'nowrap', lineHeight: 1.3, fontSize: '9px', fontWeight: '600', color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '0.5px', border: '1px solid #D4AF37', borderRadius: '4px', padding: '1px 4px' };
const countBadge = { flex: '0 0 auto', minWidth: 20, textAlign: 'center', lineHeight: '20px', fontSize: '11px', fontWeight: '700', color: '#1a1a1a', backgroundColor: '#D4AF37', borderRadius: '10px', padding: '0 6px' };
const subButton = { ...button, minHeight: 38, padding: '9px 14px 9px 28px', fontSize: '13px', fontWeight: '500' };
const active = { background: 'linear-gradient(180deg, #f6e7b4 0%, #d9b357 42%, #b98d2e 58%, #e8cc7a 100%)', color: '#1a1a1a', borderColor: '#b8903a' };

// True when the screen is narrower than a tablet. Updates when the window is resized or rotated.
export const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isPhone;
};

export default function AppShell({ children, adminActive }) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { user, userRole, logout } = auth;

  // Keeps existing seller deals' task dates on the current task logic (see TASK_LOGIC_VERSION).
  useEffect(() => {
    if (!user?.uid) return;
    refreshSellerTaskDates(user.uid).catch((err) => console.error('Could not refresh seller task dates', err));
  }, [user?.uid]);
  const isAdmin = userRole === 'Admin';
  const crmId = crmIdentity(auth);
  const crmBlocked = isCrmBlocked(userRole, crmId.uid, crmId.email);
  const crmOn = canUseCrm(userRole, crmId.uid, crmId.email);
  const ytAccess = useYoutubeStudioAccess();
  // YouTube Studio: hidden entirely for Amanda (and while loading), "Coming soon" badge for everyone without access.
  const withYt = (it) => (!it.children ? it : { ...it, children: it.children.filter((c) => !c.youtubeStudio || (ytAccess && ytAccess !== 'hidden')).map((c) => (c.youtubeStudio && ytAccess === 'comingSoon' ? { ...c, comingSoon: true } : c)) });
  const roleItems = ITEMS.filter((it) => !isPathBlockedForRole(userRole, it.path) && !(crmBlocked && it.crm) && (!it.kellyOnly || isKelly(user?.uid))).map(withYt).map((it) => (it.children && it.children.length === 1 ? { label: it.label, path: it.path } : it));
  const menuItems = crmOn ? roleItems : roleItems.map((it) => (it.crm ? { label: it.label, path: it.path, comingSoon: true } : it));
  const inboxUnread = useInboxUnread(user?.uid, crmOn);
  const isPhone = useIsPhone();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    recordPageView();
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [menuOpen]);
  const signOut = async () => { try { await logout(); navigate('/'); } catch (err) { console.error('Logout failed:', err); } };

  // Which menu groups (Leads) are expanded. The group for the current page opens itself.
  const [openGroups, setOpenGroups] = useState(() => ({ [location.pathname.split('/')[1]]: true }));
  useEffect(() => {
    const g = location.pathname.split('/')[1];
    setOpenGroups((o) => (o[g] ? o : { ...o, [g]: true }));
  }, [location.pathname]);

  const isActive = (item) => !adminActive && (location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path)));

  // One menu entry. Leads is the CRM: it expands to show its pages underneath.
  const renderItem = (item) => {
    if (!item.children) {
      return (
        <button className={`app-nav-btn${isActive(item) ? ' app-nav-active' : ''}`} key={item.path} type="button" onClick={(e) => { e.currentTarget.blur(); navigate(item.path); }} style={{ ...button, ...rowLine, ...(isActive(item) ? active : {}) }}>
          <span style={labelTrunc}>{item.label}</span>
          {item.comingSoon && <span style={soonBadge}>Coming soon</span>}
          {item.inbox && !item.comingSoon && inboxUnread > 0 && <span style={countBadge} aria-label={`${inboxUnread} unread`}>{inboxUnread > 99 ? '99+' : inboxUnread}</span>}
        </button>
      );
    }
    const groupActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return (
      <React.Fragment key={item.path}>
        <button className={`app-nav-btn${groupActive ? ' app-nav-active' : ''}`} type="button" aria-expanded={!!openGroups[item.path.slice(1)]}
          onClick={(e) => { e.currentTarget.blur(); const k = item.path.slice(1); if (groupActive) { setOpenGroups((o) => ({ ...o, [k]: !o[k] })); } else { setOpenGroups((o) => ({ ...o, [k]: true })); navigate(item.children[0].path); } }}
          style={{ ...button, display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...(groupActive ? active : {}) }}>
          <span style={labelTrunc}>{item.label}</span>
          <span aria-hidden="true" style={{ fontSize: '11px' }}>{openGroups[item.path.slice(1)] ? '\u25B2' : '\u25BC'}</span>
        </button>
        {openGroups[item.path.slice(1)] && item.children.map((child) => {
          const on = location.pathname === child.path || (child.path !== item.path && location.pathname.startsWith(child.path + '/'));
          return (
            <button className={`app-nav-btn${on ? ' app-nav-active' : ''}`} key={child.path} type="button" onClick={(e) => { e.currentTarget.blur(); navigate(child.path); }} style={{ ...subButton, ...rowLine, ...(on ? active : {}) }}>
              <span style={labelTrunc}>{child.label}</span>
              {child.comingSoon && <span style={soonBadge}>Coming soon</span>}
            </button>
          );
        })}
      </React.Fragment>
    );
  };

  if (isPhone) {
    return (
      <div className="app-phone-shell" style={{ minHeight: '100vh', backgroundColor: '#000000' }}>
        <style>{NAV_CSS}</style>
        <header style={topBar} className="app-phone-header">
          <button type="button" onClick={() => setMenuOpen(true)} style={menuToggle} aria-label="Open menu" aria-expanded={menuOpen}>
            <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1 }}>{'\u2630'}</span>
          </button>
          <img src="/resi-logo-white.png" alt="Resi" style={{ height: 30, width: 'auto', maxWidth: 120 }} />
          <span style={{ width: 44 }} aria-hidden="true" />
        </header>
        <div className={`app-drawer-backdrop${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(false)} aria-hidden="true" />
        <nav className={`app-drawer${menuOpen ? ' open' : ''}`} style={drawer} aria-hidden={!menuOpen} aria-label="Main menu">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <img src="/resi-logo-white.png" alt="Resi" style={{ height: 30, width: 'auto', maxWidth: 120 }} />
            <button type="button" onClick={() => setMenuOpen(false)} style={menuToggle} aria-label="Close menu">{'\u2715'}</button>
          </div>
          <div style={label}>Menu</div>
          {menuItems.map(renderItem)}
          {isAdmin && (
            <button type="button" className={`app-nav-btn${adminActive ? ' app-nav-active' : ''}`} onClick={() => navigate('/dashboard?admin=1')} style={{ ...button, ...(adminActive ? active : {}) }}>
              Admin Dashboard
            </button>
          )}
          <ViewAsToggle inline />
          <button type="button" onClick={signOut} style={{ ...button, color: '#999999', marginTop: 8 }}>Sign Out</button>
        </nav>
        <main className="app-main" style={{ minWidth: 0 }}>{children}</main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#000000' }}>
      <style>{NAV_CSS}</style>
      <aside style={sideNav}>
        <img src="/resi-logo-white.png" alt="Resi" style={{ height: 40, width: 'auto', maxWidth: 160, marginBottom: 20, alignSelf: 'flex-start' }} />
        {menuItems.map(renderItem)}
        {isAdmin && (
          <button type="button" className={`app-nav-btn${adminActive ? ' app-nav-active' : ''}`} onClick={() => navigate('/dashboard?admin=1')} style={{ ...button, ...(adminActive ? active : {}) }}>
            Admin Dashboard
          </button>
        )}
        <button type="button" onClick={signOut} style={{ ...button, color: '#999999', marginTop: 8 }}>Sign Out</button>
      </aside>
      <div className="app-main-desktop" style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}